import { Currency, UserPayment } from '@/api/gql/graphql';
import type { SubscriptionPackagesOutput } from '@/api/gql/graphql';
import {
  useCreatePackage,
  useDeletePackage,
  useGetPackages,
  useUpdatePackage,
} from '@/api/hooks/usePackages';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';
import { ColumnDefinition } from '@/components/Ui/DataTable/DataTable.type';
import {
  PackageFormValues,
  useHandlePackageForm,
} from '@/hooks/usePackageForm';
import { useToast } from '@/hooks/useToast';
import { useCouponStore } from '@/store/CouponStore/CouponStore';
import { usePackageStore } from '@/store/PackageStore/PackageStore';

const packageColums: ColumnDefinition[] = [
  { field: 'type', header: 'Nom' },
  { field: 'duration', header: 'Durée' },
  { field: 'pricing', header: 'Tarif' },
  { field: 'status', header: 'Statut' },
  { field: 'action', header: 'Action', sortable: false },
];

export enum PackageStatus {
  INACTIVE = 'false',
  ACTIVE = 'true',
}

export const packageConfig: Record<
  PackageStatus,
  { label: string; color: string }
> = {
  [PackageStatus.INACTIVE]: { label: 'Inactif', color: 'text-completed' },
  [PackageStatus.ACTIVE]: { label: 'Actif', color: 'text-running' },
};

export const usePackage = () => {
  const {
    isOpenPackageFormModal,
    openPackageFormModal,
    subsPackage,
    setSubsPackage,
    packageDeletionModal,
    openPackageDeletionModal,
    packageListLimit,
    setPackageListLimit,
  } = usePackageStore();
  const { setFormMode } = useCouponStore();
  const { showToast } = useToast();
  const { register, handleSubmit, errors, reset, setValue } =
    useHandlePackageForm('create');
  const {
    register: registerUpdateForm,
    handleSubmit: handleSubmitUpdate,
    errors: updateErrors,
    setValue: setUpdateValue,
    reset: resetUpdate,
  } = useHandlePackageForm('update');

  const { data: packagesList, isLoading: loadPackages } = useGetPackages();
  const { mutate: createPackage, isPending: isCreatePending } =
    useCreatePackage();
  const { mutate: updatePackage, isPending: isUpdatePending } =
    useUpdatePackage();

  const { mutate: deletePackage, isPending: isDeletePending } =
    useDeletePackage();

  const packages = packagesList?.map((item: SubscriptionPackagesOutput) => {
    return {
      _id: item._id,
      name: item.description,
    };
  });

  // Aligné sur l'enum UserPayment du schéma (PRO n'existe plus,
  // PRO_TRIMESTRE est devenu PRO_TRIMESTER)
  const packageType = [
    { _id: UserPayment.Free, name: UserPayment.Free },
    { _id: UserPayment.ProMonth, name: UserPayment.ProMonth },
    { _id: UserPayment.ProTrimester, name: UserPayment.ProTrimester },
    { _id: UserPayment.ProWeek, name: UserPayment.ProWeek },
    { _id: UserPayment.ProYear, name: UserPayment.ProYear },
  ];

  const currency = [
    { _id: Currency.Xaf, name: Currency.Xaf },
    { _id: Currency.Xof, name: Currency.Xof },
  ];

  const openUpdatePackage = () => {
    openPackageFormModal();
    setFormMode('update');
  };

  const openCreatePackage = () => {
    openPackageFormModal();
    setFormMode('create');
  };

  const openDeletePackage = () => {
    openPackageDeletionModal();
  };

  const activateOrDeactivatePackage = () => {
    updatePackage(
      {
        input: {
          isActive: subsPackage?.isActive === false ? true : false,
        },
        updateSubscriptionPackageId: subsPackage._id,
      },
      {
        onSuccess: () => {
          showToast(
            `Package ${!subsPackage?.isActive === true ? 'Activer' : 'Desactiver'}`,
            'success',
            {
              autoClose: 3000,
              position: 'top-right',
            },
          );
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const packageMenuItems = [
    createMenuItem('Modifier', '', openUpdatePackage),
    createMenuItem(
      `${!subsPackage?.isActive === true ? 'Activer' : 'Desactiver'}`,
      '',
      activateOrDeactivatePackage,
    ),
    createMenuItem('Supprimer', 'font-[400] text-[#EA242C]', openDeletePackage),
  ];

  const onCreatePackage = (values: PackageFormValues) => {
    createPackage(
      {
        pricing: values.price?.toString() ?? '',
        title: values.packageName ?? '',
        type: values.packageType as UserPayment,
        duration: values.duration ?? 0,
        description: values.description ?? '',
        currency: values.currency as Currency,
      },
      {
        onSuccess: () => {
          showToast('Package créé', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });

          reset();
          openPackageFormModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const onUpdatePackage = (values: PackageFormValues) => {
    updatePackage(
      {
        input: {
          pricing: values.price?.toString() ?? '0',
          title: values.packageName ?? '',
          type: values.packageType as UserPayment,
          duration: values.duration ?? 0,
          description: values.description ?? '',
          currency: values.currency as Currency,
        },
        updateSubscriptionPackageId: subsPackage._id,
      },
      {
        onSuccess: () => {
          showToast('Package mis à jour', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });

          resetUpdate();
          openPackageFormModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const onDeletePackage = () => {
    deletePackage(
      {
        removeSubscriptionPackageId: subsPackage._id,
      },
      {
        onSuccess: () => {
          showToast('Package supprimé', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          openPackageDeletionModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  return {
    register,
    handleSubmit,
    errors,
    reset,
    setValue,
    packages,
    packageType,
    loadPackages,
    packagesList,
    packageMenuItems,
    openCreatePackage,
    packageConfig,
    packageColums,
    isUpdatePending,
    isCreatePending,
    registerUpdateForm,
    handleSubmitUpdate,
    updateErrors,
    resetUpdate,
    setUpdateValue,
    onCreatePackage,
    onUpdatePackage,
    isOpenPackageFormModal,
    openPackageFormModal,
    currency,
    subsPackage,
    setSubsPackage,
    isDeletePending,
    onDeletePackage,
    packageDeletionModal,
    openPackageDeletionModal,
    packageListLimit,
    setPackageListLimit,
  };
};
